import { Route, Routes } from 'react-router-dom';

import RoleGuard from './components/auth/RoleGuard';
import ErrorBoundary from './components/ErrorBoundary';
import Layout from './components/layout/Layout';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { Toaster } from './components/ui/toaster';
import { useAuth } from './hooks/useAuth';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import AWBCreatePageNew from './pages/AWBCreatePageNew';
import AWBEditPageNew from './pages/AWBEditPageNew';
import AWBManagement from './pages/AWBManagement';
import AwbStateManagement from './pages/AwbStateManagement';
import AwbStatusManagement from './pages/AwbStatusManagement';
import AWBViewPageNew from './pages/AWBViewPageNew';
import BillingManagement from './pages/BillingManagement';
import CityManagement from './pages/CityManagement';
import CompanyManagement from './pages/CompanyManagement';
import CustomerManagement from './pages/CustomerManagement';
import Dashboard from './pages/Dashboard';
import DispatchManagement from './pages/DispatchManagement';
import DistrictManagement from './pages/DistrictManagement';
import FreeFormItemManagement from './pages/FreeFormItemManagement';
import FreeFormNoteManagement from './pages/FreeFormNoteManagement';
import IncotermManagement from './pages/IncotermManagement';
import LicenseLocationManagement from './pages/LicenseLocationManagement';
import LicenseManagement from './pages/LicenseManagement';
import Login from './pages/Login';
import ManifestManagement from './pages/ManifestManagement';
import MasterDataManagement from './pages/MasterDataManagement';
import NotFound from './pages/NotFound';
import PincodeManagement from './pages/PincodeManagement';
import ProductFormatManagement from './pages/ProductFormatManagement';
import ReportsAnalytics from './pages/ReportsAnalytics';
import ServiceTypeManagement from './pages/ServiceTypeManagement';
import SettingsPage from './pages/SettingsPage';
import StateManagement from './pages/StateManagement';
import TrackingManagement from './pages/TrackingManagement';
import TrackingPage from './pages/TrackingPage';
import UserManagement from './pages/UserManagement';
import VendorTypeManagement from './pages/VendorTypeManagement';
import ZoneManagement from './pages/ZoneManagement';

function App() {
  const { user, isLoading } = useAuth();

  // Enable session timeout for authenticated users
  useSessionTimeout({
    timeoutMinutes: 30,
    warningMinutes: 5,
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  // Check if we're on the public tracking page - allow access without authentication
  const isPublicTrackingPage = window.location.pathname === '/track';
  
  if (isPublicTrackingPage) {
    return (
      <ErrorBoundary>
        <div className='min-h-screen bg-background'>
          <Routes>
            <Route path='/track' element={<TrackingPage />} />
          </Routes>
          <Toaster />
        </div>
      </ErrorBoundary>
    );
  }

  if (!user) {
    return (
      <ErrorBoundary>
        <Login />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <div className='min-h-screen bg-background'>
        <Layout>
          <Routes>
            <Route path='/' element={<Dashboard />} />
            <Route path='/dashboard' element={<Dashboard />} />
            <Route
              path='/users'
              element={
                <RoleGuard
                  allowedRoles={['SUPERADMIN', 'MANAGER', 'DIRECTOR']}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <UserManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/master-data'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <MasterDataManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/customers'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                    'SALES',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <CustomerManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/companies'
              element={
                <RoleGuard
                  allowedRoles={['SUPERADMIN', 'MANAGER', 'DIRECTOR']}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <CompanyManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/awb'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'DATA_ENTRY',
                    'OPERATION',
                    'CUSTOMER_SERVICES',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <AWBManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/awb/create'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'DATA_ENTRY',
                    'OPERATION',
                    'CUSTOMER_SERVICES',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <AWBCreatePageNew />
                </RoleGuard>
              }
            />
            <Route
              path='/awb/:awbNumber/view'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'DATA_ENTRY',
                    'OPERATION',
                    'CUSTOMER_SERVICES',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <AWBViewPageNew />
                </RoleGuard>
              }
            />
            <Route
              path='/awb/:awbNumber/edit'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'DATA_ENTRY',
                    'OPERATION',
                    'CUSTOMER_SERVICES',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <AWBEditPageNew />
                </RoleGuard>
              }
            />
            <Route
              path='/manifests'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'OPERATION',
                    'MANAGER',
                    'SUPERVISOR',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <ManifestManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/tracking'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'OPERATION',
                    'SUPERVISOR',
                    'CUSTOMER_SERVICES',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <TrackingManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/billing'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'BILLING',
                    'MANAGER',
                    'DIRECTOR',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <BillingManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/zones'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <ZoneManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/states'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <StateManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/districts'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <DistrictManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/dispatches'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <DispatchManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/pincodes'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <PincodeManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/product-formats'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <ProductFormatManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/service-types'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <ServiceTypeManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/vendor-types'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <VendorTypeManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/free-form-items'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <FreeFormItemManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/free-form-notes'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <FreeFormNoteManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/incoterms'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <IncotermManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/awb-states'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <AwbStateManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/awb-statuses'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <AwbStatusManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/licenses'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <LicenseManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/license-locations'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <LicenseLocationManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/cities'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'DATA_ENTRY',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <CityManagement />
                </RoleGuard>
              }
            />
            <Route
              path='/reports'
              element={
                <RoleGuard
                  allowedRoles={[
                    'SUPERADMIN',
                    'MANAGER',
                    'DIRECTOR',
                    'BILLING',
                  ]}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <ReportsAnalytics />
                </RoleGuard>
              }
            />
            <Route path='/track' element={<TrackingPage />} />
            <Route
              path='/settings'
              element={
                <RoleGuard
                  allowedRoles={['SUPERADMIN', 'MANAGER']}
                  fallback={
                    <div className='flex items-center justify-center h-64'>
                      <p className='text-muted-foreground'>Access denied</p>
                    </div>
                  }
                >
                  <SettingsPage />
                </RoleGuard>
              }
            />
            <Route path='*' element={<NotFound />} />
          </Routes>
        </Layout>
        <Toaster />
      </div>
    </ErrorBoundary>
  );
}

export default App;
